"use client"

import { useCallback, useMemo } from "react"
import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { getBasePathUrl } from "@/lib/base-path"
import { requestTokenDueToAuthFailure } from "@/lib/app-messaging"
import { tokenManager } from "@/lib/token-manager"

// API endpoints
export const API_ENDPOINTS = {
  // Main monolith API
  MAIN: {
    BASE_URL: env.apiUrl,
    // Add specific endpoints here    
    USERS: "/users"
  },
  // Admin Service API
  ADMIN: {
    BASE_URL: env.adminServiceApiEndpoint,
    // Add specific endpoints here
    SETTINGS: "/settings",
    // ...
  },
  // Commission Service API
  COMMISSION: {
    BASE_URL: env.commissionServiceApiUrl,
    // Add specific endpoints here
    TRANSACTION: {
      UNPAID: "/commissiontransaction/unpaid",
      SCHEDULE: "/commissiontransaction/schedule",
      DEFER: "/commissiontransaction/defer",
      GENERATE_RECURRING: "/commissiontransaction/generate-recurring",
      USERS: "/commissiontransaction/users"
    },
    STATEMENT: {
      OPENING_BALANCES: "/commissionstatement/opening-balances"
    },
    PAYOUT: {
      LATEST_PAYOUT: "/commissionpayout/latest",
      PAYOUT: "/commissionpayout/",
      PAYOUT_HISTORY: "/commissionpayout/history",
      PAYOUT_YEARS: "/commissionpayout/years",
      PAYOUT_DETAILS: "/commissionpayout",
      CURRENT_PERIOD: "/commissionpayout/current-period",
      FINALIZE_PERIOD: "/commissionpayout/finalize-period"
    }
  }
}

// Log API endpoints for debugging
if (typeof window !== "undefined") {
  console.log("API_ENDPOINTS:", API_ENDPOINTS)
}

// Custom hook for making authenticated API requests
export function useApi() {
  const { token } = useAuth()

  // Generic fetch function with authentication and token retry logic
  const fetchWithAuth = useCallback(async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
    const headers = new Headers(options.headers)

    // Add authorization header if token exists
    if (token) {
      headers.set("Authorization", `Bearer ${token}`)
    }

    // Apply base path if the URL is relative and doesn't start with http or //
    let finalUrl = url
    if (!url.startsWith("http") && !url.startsWith("//")) {
      finalUrl = getBasePathUrl(url)
    }

    try {
      const response = await fetch(finalUrl, {
        ...options,
        headers,
      })

      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          console.warn("Received 401 Unauthorized from", finalUrl)

          // Only request a new token if we haven't already requested one
          if (tokenManager.shouldRequestToken()) {
            console.log("Requesting new token from parent")
            requestTokenDueToAuthFailure(`401 Unauthorized from ${finalUrl}`)
          }

          // Queue this request to be retried when we get a new token
          console.log("Queueing request for retry when new token is received")
          const retriedResponse = await tokenManager.queueRequest(finalUrl, {
            ...options,
            headers,
          })

          // Process the retried response
          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => ({}))
            throw new Error(errorData.message || `API request failed with status ${retriedResponse.status}`)
          }

          // For non-JSON responses, return the response object
          const contentType = retriedResponse.headers.get("content-type")
          if (contentType && contentType.includes("application/json")) {
            return retriedResponse.json()
          }

          return retriedResponse as unknown as T
        }

        // Handle other error responses
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `API request failed with status ${response.status}`)
      }

      // For non-JSON responses, return the response object
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return response.json()
      }

      return response as unknown as T
    } catch (error) {
      // Rethrow the error
      throw error
    }
  }, [token])

  // Helper methods for common HTTP methods
  const get = useCallback(<T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "GET" }), [fetchWithAuth])

  const post = useCallback(<T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    }), [fetchWithAuth])

  const put = useCallback(<T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    }), [fetchWithAuth])

  const del = useCallback(<T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" }), [fetchWithAuth])

  // Create full URLs for each API
  const createUrl = useCallback((baseUrl: string, path: string) => {
    // Remove trailing slash from base URL if present
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    // Remove leading slash from path if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path
    return `${base}/${cleanPath}`
  }, [])

  // API-specific methods
  const mainApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
  }), [get, post, put, del, createUrl])

  const adminApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
  }), [get, post, put, del, createUrl])

  const commissionServiceApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.COMMISSION.BASE_URL, path), options),
  }), [get, post, put, del, createUrl])

  return useMemo(() => ({
    mainApi,
    adminApi,
    commissionServiceApi,
    fetchWithAuth,
  }), [mainApi, adminApi, commissionServiceApi, fetchWithAuth])
}
